import { Link } from 'react-router-dom'
import { Shield, Lock, Eye, EyeOff, Database, Trash2, ExternalLink } from 'lucide-react'

/**
 * Privacy & Safety Tab
 * Displays GJYL's privacy standards and links to full policies
 */
export default function PrivacyTab({ profile, onUpdateProfile }) {
  return (
    <div className="p-6 max-w-4xl space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">Privacy & Safety</h3>
        <p className="text-sm text-[#64748b]">Your privacy is fundamental to how GJYL is built.</p>
      </div>

      {/* Privacy Standards */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#00ad9f]" />
          Our Privacy Standards
        </h4>

        <div className="grid gap-4">
          {/* End-to-End Encryption */}
          <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#00ad9f]/10 rounded-lg">
                <Lock className="w-5 h-5 text-[#00ad9f]" />
              </div>
              <div>
                <h5 className="font-medium text-white mb-1">End-to-End Encryption</h5>
                <p className="text-sm text-[#64748b]">
                  Your messages are encrypted on your device. Only you and your recipients can read them -
                  not us, not anyone else.
                </p>
              </div>
            </div>
          </div>

          {/* Zero-Knowledge Architecture */}
          <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#00ad9f]/10 rounded-lg">
                <EyeOff className="w-5 h-5 text-[#00ad9f]" />
              </div>
              <div>
                <h5 className="font-medium text-white mb-1">Zero-Knowledge Architecture</h5>
                <p className="text-sm text-[#64748b]">
                  We cannot read your messages. Our servers only handle encrypted data - we have zero
                  knowledge of your message content.
                </p>
              </div>
            </div>
          </div>

          {/* No Ads or Tracking */}
          <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#00ad9f]/10 rounded-lg">
                <Eye className="w-5 h-5 text-[#00ad9f]" />
              </div>
              <div>
                <h5 className="font-medium text-white mb-1">No Ads, No Tracking, No Data Sales</h5>
                <p className="text-sm text-[#64748b]">
                  We don't show ads. We don't track your behavior. We never sell or share your data
                  with third parties for marketing. Your attention isn't our product.
                </p>
              </div>
            </div>
          </div>

          {/* Minimal Data Collection */}
          <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#00ad9f]/10 rounded-lg">
                <Database className="w-5 h-5 text-[#00ad9f]" />
              </div>
              <div>
                <h5 className="font-medium text-white mb-1">Minimal Data Collection</h5>
                <p className="text-sm text-[#64748b]">
                  We only collect what's necessary: your email for authentication, and encrypted messages
                  for delivery. No location tracking, no contact harvesting, no AI training on your data.
                </p>
              </div>
            </div>
          </div>

          {/* Data Deletion */}
          <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#00ad9f]/10 rounded-lg">
                <Trash2 className="w-5 h-5 text-[#00ad9f]" />
              </div>
              <div>
                <h5 className="font-medium text-white mb-1">Your Data, Your Control</h5>
                <p className="text-sm text-[#64748b]">
                  Messages are automatically deleted after 30 days. You can delete your account anytime,
                  and we'll permanently remove all your data within 30 days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Settings Coming Soon */}
      <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-6">
        <div className="text-center">
          <div className="text-3xl mb-3">🔧</div>
          <h4 className="text-lg font-medium text-white mb-2">Privacy Controls Coming Soon</h4>
          <p className="text-sm text-[#64748b] max-w-md mx-auto mb-3">
            Fine-grained controls for friend requests, DM settings, profile visibility,
            activity status, and blocked users management.
          </p>
          <div className="px-3 py-1.5 bg-[#00ad9f]/20 text-[#00ad9f] rounded text-xs font-semibold inline-block">
            Coming Soon
          </div>
        </div>
      </div>

      {/* Legal Documents */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-white">Legal Documents</h4>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/privacy"
            target="_blank"
            className="flex items-center gap-2 px-4 py-3 bg-[#0a1628] border border-[#1e3a5f] rounded-lg hover:border-[#00ad9f] transition-colors group"
          >
            <Shield className="w-4 h-4 text-[#64748b] group-hover:text-[#00ad9f]" />
            <span className="text-sm text-white">Privacy Policy</span>
            <ExternalLink className="w-3 h-3 text-[#64748b]" />
          </Link>
          <Link
            to="/terms"
            target="_blank"
            className="flex items-center gap-2 px-4 py-3 bg-[#0a1628] border border-[#1e3a5f] rounded-lg hover:border-[#00ad9f] transition-colors group"
          >
            <svg className="w-4 h-4 text-[#64748b] group-hover:text-[#00ad9f]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm text-white">Terms of Service</span>
            <ExternalLink className="w-3 h-3 text-[#64748b]" />
          </Link>
        </div>
        <p className="text-xs text-[#64748b]">
          GJYL complies with GDPR, CPRA, COPPA, and Google's API Services User Data Policy.
        </p>
      </div>
    </div>
  )
}
